import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TouchButton } from './TouchButton';

// Navigation icons
const DashboardIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const ChatIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const TransactionsIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// TEMPORARILY HIDDEN FOR MVP FOCUS - See docs/features/mvp-features-restoration.md to restore
// const GoalsIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
//   <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
//     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
//   </svg>
// );

const MoreIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

export default function BottomNavigation() {
  const location = useLocation();
  const { t } = useTranslation('common');

  const navItems = [
    {
      path: '/dashboard',
      label: t('navigation.dashboard'),
      shortLabel: t('navigation.dashboard'),
      Icon: DashboardIcon,
      exact: true
    },
    {
      path: '/dashboard/chat',
      label: t('navigation.chat'),
      shortLabel: 'Chat',
      Icon: ChatIcon
    },
    {
      path: '/dashboard/transactions',
      label: t('navigation.transactions'),
      shortLabel: 'Txns',
      Icon: TransactionsIcon
    },
    // TEMPORARILY HIDDEN FOR MVP FOCUS - See docs/features/mvp-features-restoration.md to restore
    // {
    //   path: '/dashboard/goals',
    //   label: t('navigation.goals'),
    //   shortLabel: t('navigation.goals'),
    //   Icon: GoalsIcon
    // },
    {
      path: '/dashboard/more',
      label: t('navigation.more'),
      shortLabel: t('navigation.more'),
      Icon: MoreIcon
    },
  ];

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-mobile-bottom-nav"
      style={{
        background: 'var(--bg-card-solid)',
        borderTop: '1px solid var(--border-cyan-10)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      role="navigation"
      aria-label="Mobile bottom navigation"
    >
      <div className="flex items-stretch justify-evenly px-1 pt-1 pb-1">
        {navItems.map((item) => {
          const Icon = item.Icon;
          const active = isActive(item.path, item.exact);

          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex-1 min-w-0 overflow-hidden"
            >
              <TouchButton
                variant={active ? 'primary' : 'ghost'}
                size="sm"
                className="w-full flex flex-col items-center justify-center transition-all duration-200"
                style={{
                  borderRadius: 'var(--sol-radius-lg)',
                  color: active ? 'var(--cyan)' : 'var(--text-secondary)',
                  background: active ? '#F4F4F5' : 'transparent',
                  // Override TouchButton's default 8px 16px padding — too wide for 4 nav items
                  padding: '6px 4px',
                  minHeight: '48px',
                  gap: '2px',
                }}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span
                  className={active ? 'font-semibold' : 'font-normal'}
                  style={{
                    fontSize: '10px',
                    lineHeight: 1.2,
                    width: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.shortLabel}
                </span>
              </TouchButton>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
